// Melee archetype (MOB-2): hostiles that acquire a player within followRange,
// steer/jump toward it (MOB-3), and strike on a cooldown (MOB-5, PLR-3). PURE.
import { MOB } from '../../config';
import type { MobDef } from '../../registry/schemas';
import { halfOf, type Entity, type MobContext } from '../entity';
import { wander, moveToward, tickSpeed, difficultyScale } from './shared';

/** Acquire + chase + strike the nearest player. Returns true if it has a target
 *  (so overrides like the creeper can layer their own logic on top). */
export function meleeAI(e: Entity, def: MobDef, ctx: MobContext): boolean {
  const { frame, sys } = ctx;
  const scale = difficultyScale(frame.difficulty);
  if (scale === 0) { wander(e, ctx, def); return false; } // peaceful: no hostility

  const near = sys.nearestPlayer(frame, e.x, e.y, e.z, def.followRange);
  if (!near) { e.targetIdx = -1; wander(e, ctx, def); return false; }

  e.targetIdx = near.idx;
  const pl = frame.players[near.idx]!;
  const reach = halfOf(e, def) + 0.3 + MOB.MELEE.REACH_PAD;
  const flat = Math.hypot(pl.x - e.x, pl.z - e.z);

  if (flat <= reach && Math.abs(pl.y - e.y) < 2) {
    // In range: face the target and attack on cooldown.
    e.yaw = Math.atan2(-(pl.x - e.x), -(pl.z - e.z));
    if (e.attackCooldown === 0 && def.attackDamage) {
      pl.hurt(def.attackDamage * scale, pl.x - e.x, pl.z - e.z);
      // A husk's bite inflicts hunger (onHitEffect, chance-gated).
      const eff = def.onHitEffect;
      if (eff && ctx.rng.nextFloat() < eff.chance) pl.addEffect?.(eff.effect, eff.amplifier, Math.round(eff.seconds * 20));
      e.attackCooldown = MOB.MELEE.COOLDOWN;
    }
  } else {
    moveToward(e, ctx, pl.x, pl.z, tickSpeed(def, 1));
  }
  return true;
}

export function melee(e: Entity, def: MobDef, ctx: MobContext): void {
  meleeAI(e, def, ctx);
}
